import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Two-step "forgot password" flow: request OTP → submit OTP + new password.
 * Lives inside the AuthModal dialog. */
export default function ForgotPasswordPanel({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If this email is registered, a reset code has been sent.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send code");
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: newPw });
      toast.success("Password reset, you can now sign in.");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-7" data-testid="forgot-password-panel">
      <button onClick={onBack} className="text-sm underline mb-3" style={{ color: "var(--md-text-soft)" }} data-testid="forgot-back">← Back to sign in</button>
      <div className="md-label-eyebrow mb-2">Forgot password</div>
      <h2 className="font-serif text-3xl mb-1">{step === 1 ? "Let's reset that." : "Enter the code."}</h2>
      <p className="text-sm mb-5" style={{ color: "var(--md-text-soft)" }}>
        {step === 1
          ? "We'll email you a 6-digit code valid for 15 minutes."
          : `Sent to ${email}. Check your inbox (and spam).`}
      </p>

      {step === 1 ? (
        <form onSubmit={requestOtp} className="space-y-3">
          <div>
            <label className="md-label-eyebrow block mb-1.5">Email</label>
            <input type="email" required className="md-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-submit">
            {busy ? "Sending…" : "Send reset code"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitReset} className="space-y-3">
          <div>
            <label className="md-label-eyebrow block mb-1.5">6-digit code</label>
            <input required pattern="\d{6}" inputMode="numeric" maxLength={6} className="md-input tracking-[0.4em] text-center text-lg" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="forgot-otp" />
          </div>
          <div>
            <label className="md-label-eyebrow block mb-1.5">New password (min 6 chars)</label>
            <input required type="password" minLength={6} className="md-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="forgot-new-pw" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-reset-submit">
            {busy ? "Resetting…" : "Reset password"}
          </button>
          <button type="button" onClick={() => setStep(1)} className="text-xs underline w-full text-center mt-2" style={{ color: "var(--md-text-muted)" }} data-testid="forgot-resend">Didn&apos;t get the code? Try again</button>
        </form>
      )}
    </div>
  );
}
